import apiService from '../services/apiService';

export interface PerformanceMetrics {
  timestamp: string;
  sessionId: string;
  recordingDuration: number;
  analysisWaitTime: number;
  analysisMode: 'quick' | 'detailed' | 're-analysis';
  analysisStages: {
    audioProcessing: number;
    phonemeAnalysis: number;
    transcription: number;
    statsCalculation: number;
    total: number;
  };
  browserInfo: string;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  networkSpeed: string;
  errorOccurred: boolean;
  errorMessage?: string;
}

export class PerformanceLogger {
  private static instance: PerformanceLogger;
  private sessionId: string;
  private timings: Map<string, number>;
  private stageDurations: Map<string, number>;
  
  private constructor() {
    this.sessionId = this.generateSessionId();
    this.timings = new Map();
    this.stageDurations = new Map();
  }
  
  static getInstance(): PerformanceLogger {
    if (!PerformanceLogger.instance) {
      PerformanceLogger.instance = new PerformanceLogger();
    }
    return PerformanceLogger.instance;
  }
  
  private generateSessionId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }
  
  startTiming(stage: string): void {
    this.timings.set(stage, performance.now());
  }
  
  endTiming(stage: string): number {
    const startTime = this.timings.get(stage);
    if (startTime) {
      const duration = (performance.now() - startTime) / 1000; // Convert to seconds
      this.stageDurations.set(stage, duration);
      this.timings.delete(stage);
      return duration;
    }
    return 0;
  }
  
  private getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile/i.test(userAgent) && !/ipad/i.test(userAgent)) {
      return 'mobile';
    }
    if (/ipad|tablet/i.test(userAgent)) {
      return 'tablet';
    }
    return 'desktop';
  }
  
  private getNetworkSpeed(): string {
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    
    if (connection) {
      if (connection.effectiveType) {
        return connection.effectiveType;
      }
      if (connection.type) {
        return connection.type;
      }
    }
    
    return 'unknown';
  }
  
  async logPerformance(
    recordingDuration: number,
    errorOccurred: boolean = false,
    errorMessage?: string,
    analysisMode: 'quick' | 'detailed' | 're-analysis' = 'quick'
  ): Promise<void> {
    const metrics: PerformanceMetrics = {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      recordingDuration,
      analysisWaitTime: this.stageDurations.get('totalAnalysis') || 0,
      analysisMode,
      analysisStages: {
        audioProcessing: this.stageDurations.get('audioProcessing') || 0,
        phonemeAnalysis: this.stageDurations.get('phonemeAnalysis') || 0,
        transcription: this.stageDurations.get('transcription') || 0,
        statsCalculation: this.stageDurations.get('statsCalculation') || 0,
        total: this.stageDurations.get('totalAnalysis') || 0
      },
      browserInfo: navigator.userAgent,
      deviceType: this.getDeviceType(),
      networkSpeed: this.getNetworkSpeed(),
      errorOccurred,
      errorMessage
    };
    
    // Log to console for debugging
    console.log('Performance Metrics:', metrics);
    
    // Store in localStorage for now (in production, send to server)
    this.storeLocally(metrics);
    
    // Send to server
    try {
      await this.sendToServer(metrics);
    } catch (error) {
      console.warn('Failed to send metrics to server, storing locally only:', error);
    }
    
    // Clear stage durations for next recording
    this.stageDurations.clear();
  }
  
  private storeLocally(metrics: PerformanceMetrics): void {
    try {
      const existingLogs = localStorage.getItem('voiceCoachPerformanceLogs');
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      
      // Keep only last 50 logs to prevent localStorage overflow
      if (logs.length >= 50) {
        logs.shift();
      }
      
      logs.push(metrics);
      localStorage.setItem('voiceCoachPerformanceLogs', JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to store metrics locally:', error);
    }
  }
  
  private async sendToServer(metrics: PerformanceMetrics): Promise<void> {
    try {
      const response = await apiService.logPerformance(metrics);
      
      if (!response.ok) {
        throw new Error(`Failed to log performance: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Performance logged to server:', result);
    } catch (error) {
      console.error('Failed to send metrics to server:', error);
      throw error;
    }
  }
  
  private async getIPInfo(): Promise<{ ip: string }> {
    try {
      // Using a public IP API (in production, do this server-side)
      const response = await fetch('https://api.ipify.org?format=json');
      return await response.json();
    } catch {
      return { ip: 'unknown' };
    }
  }
  
  // Utility method to get all stored logs
  getStoredLogs(): PerformanceMetrics[] {
    try {
      const logs = localStorage.getItem('voiceCoachPerformanceLogs');
      return logs ? JSON.parse(logs) : [];
    } catch {
      return [];
    }
  }
  
  // Utility method to export logs as CSV
  exportLogsAsCSV(): string {
    const logs = this.getStoredLogs();
    if (logs.length === 0) return '';
    
    // Create CSV header
    const headers = [
      'Timestamp',
      'Session ID',
      'Recording Duration (s)',
      'Analysis Wait Time (s)',
      'Analysis Mode',
      'Audio Processing (s)',
      'Phoneme Analysis (s)',
      'Transcription (s)',
      'Stats Calculation (s)',
      'Total (s)',
      'Device Type',
      'Network Speed',
      'Error Occurred',
      'Error Message'
    ];
    
    const rows = logs.map(log => [
      log.timestamp,
      log.sessionId,
      log.recordingDuration.toFixed(2),
      log.analysisWaitTime.toFixed(2),
      log.analysisMode || 'unknown',
      log.analysisStages.audioProcessing.toFixed(2),
      log.analysisStages.phonemeAnalysis.toFixed(2),
      log.analysisStages.transcription.toFixed(2),
      log.analysisStages.statsCalculation.toFixed(2),
      log.analysisStages.total.toFixed(2),
      log.deviceType,
      log.networkSpeed,
      log.errorOccurred,
      log.errorMessage || ''
    ]);
    
    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    return csv;
  }
  
  // Clear all stored logs
  clearLogs(): void {
    localStorage.removeItem('voiceCoachPerformanceLogs');
  }
}